import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, FileText, TrendingUp,
  ArrowRight, Shield, CreditCard, Activity, Circle
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { toast } from 'sonner';
import { getStats, getAdminActivity } from '@/lib/api';

const StatCard = ({ icon: Icon, label, value, sub, color = "rtb-purple" }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-5 bg-white/[0.04] border border-white/[0.06] rounded-xl hover:border-white/[0.12] transition-colors"
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-4">
      <div className={`w-10 h-10 bg-${color}/10 flex items-center justify-center rounded-lg`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      {sub && <span className={`text-${color} text-xs font-bold bg-${color}/10 px-2.5 py-1 rounded-full`}>{sub}</span>}
    </div>
    <p className="text-3xl font-oswald font-bold text-white">{value}</p>
    <p className="text-white/40 text-sm mt-1">{label}</p>
  </motion.div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getStats().catch(() => null),
      getAdminActivity().catch(() => [])
    ]).then(([s, a]) => {
      setStats(s);
      setActivity(a || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <AdminLayout title="Dashboard"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-rtb-purple border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  }

  const dailySignups = stats?.daily_signups || [];
  const phaseDist = stats?.phase_distribution || [];
  const pathDist = stats?.path_distribution || [];

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Total Participants" value={stats?.total_participants || 0} />
        <StatCard icon={CreditCard} label="Paid Enrollments" value={stats?.paid_participants || 0}
          sub={`${stats?.enrollment_rate || 0}%`} />
        <StatCard icon={FileText} label="Contracts Signed" value={stats?.contracts_signed || 0} />
        <StatCard icon={Shield} label="LLCs In Progress" value={stats?.llc_in_progress || 0} color="rtb-green" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Program Phase Distribution */}
        <div className="p-5 bg-white/[0.04] border border-white/[0.06] rounded-xl" data-testid="phase-chart">
          <h3 className="font-oswald text-base font-bold text-white mb-5">Program Progress</h3>
          {phaseDist.length === 0 ? (
            <p className="text-white/30 text-center py-8 text-sm">No enrolled participants yet</p>
          ) : (
            <div className="space-y-3">
              {['not_started', 'foundation', 'structure', 'activation', 'expansion'].map(phase => {
                const item = phaseDist.find(p => p._id === phase);
                const count = item?.count || 0;
                const total = stats?.paid_participants || 1;
                const pct = Math.round((count / total) * 100);
                const colors = {
                  not_started: 'bg-white/20', foundation: 'bg-blue-500',
                  structure: 'bg-rtb-purple', activation: 'bg-rtb-green', expansion: 'bg-emerald-400'
                };
                return (
                  <div key={phase}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="capitalize text-white/50">{phase.replace('_', ' ')}</span>
                      <span className="font-bold text-white/80">{count}</span>
                    </div>
                    <div className="w-full h-2 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className={`h-full ${colors[phase]} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recommended Paths */}
        <div className="p-5 bg-white/[0.04] border border-white/[0.06] rounded-xl" data-testid="path-chart">
          <h3 className="font-oswald text-base font-bold text-white mb-5">Business Paths</h3>
          {pathDist.length === 0 ? (
            <p className="text-white/30 text-center py-8 text-sm">No questionnaire data yet</p>
          ) : (
            <div className="space-y-3">
              {pathDist.map(p => {
                const total = pathDist.reduce((sum, x) => sum + x.count, 0) || 1;
                const pct = Math.round((p.count / total) * 100);
                const colors = {
                  real_estate: 'bg-blue-500', insurance: 'bg-rtb-green',
                  tax_services: 'bg-yellow-500', travel: 'bg-pink-500', government_contracts: 'bg-rtb-purple'
                };
                return (
                  <div key={p._id}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="capitalize text-white/50">{(p._id || '').replace('_', ' ')}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white/30 text-xs">{pct}%</span>
                        <span className="font-bold text-white/80">{p.count}</span>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className={`h-full ${colors[p._id] || 'bg-gray-500'} rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="p-5 bg-white/[0.04] border border-white/[0.06] rounded-xl" data-testid="recent-activity">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-oswald text-base font-bold text-white">Recent Activity</h3>
            <Link to="/admin/activity" className="text-rtb-purple text-xs hover:underline">View All</Link>
          </div>
          {activity.length === 0 ? (
            <p className="text-white/30 text-center py-8 text-sm">No recent activity</p>
          ) : (
            <div className="space-y-3">
              {activity.slice(0, 6).map((a, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="mt-1.5">
                    <Circle className={`w-2 h-2 ${a.action === 'login' ? 'text-rtb-green fill-rtb-green' : 'text-rtb-purple fill-rtb-purple'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white/70 truncate">{a.description}</p>
                    <p className="text-white/25 text-xs">{new Date(a.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Daily Signups */}
      <div className="p-5 bg-white/[0.04] border border-white/[0.06] rounded-xl mb-8" data-testid="daily-chart">
        <h3 className="font-oswald text-base font-bold text-white mb-5">Signups (Last 7 Days)</h3>
        {dailySignups.length === 0 ? (
          <p className="text-white/30 text-center py-8 text-sm">No recent signups</p>
        ) : (
          <div className="flex items-end gap-2 h-32">
            {dailySignups.map(d => {
              const maxC = Math.max(...dailySignups.map(x => x.count), 1);
              const h = Math.max((d.count / maxC) * 100, 8);
              return (
                <div key={d._id} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-bold text-rtb-purple">{d.count}</span>
                  <div className="w-full bg-rtb-purple/40 rounded-t-md" style={{ height: `${h}%` }} />
                  <span className="text-[10px] text-white/25">{d._id.slice(5)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Link to="/admin/leads" className="flex items-center gap-2 px-5 py-3 bg-white/[0.04] border border-white/[0.06] hover:border-rtb-purple/30 transition-colors rounded-xl text-sm">
          <Users className="w-4 h-4 text-rtb-purple" /><span className="text-white/70">Participants</span><ArrowRight className="w-3.5 h-3.5 text-white/20" />
        </Link>
        <Link to="/admin/settings" className="flex items-center gap-2 px-5 py-3 bg-white/[0.04] border border-white/[0.06] hover:border-rtb-purple/30 transition-colors rounded-xl text-sm">
          <TrendingUp className="w-4 h-4 text-rtb-purple" /><span className="text-white/70">Settings</span><ArrowRight className="w-3.5 h-3.5 text-white/20" />
        </Link>
      </div>
    </AdminLayout>
  );
}
